import React, { useEffect, useMemo, useRef, useState } from 'react';
import defaultClasses from './Calendar.module.scss';
import {
  CalendarDataType,
  CalendarHeaderNames,
  CalendarHeaderWidths,
  CellTemplateProps,
  PostingPlanCellDataType,
  QueueCellDataType
} from '../../../models/calendar';
import classNames from 'classnames';
import { StartOfWeekFormatSetting, TimeFormatSetting } from '../../../models/graphql';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useDimensions } from '../../../hooks/useDimensions';
import { useDidMount } from 'hooks/useDidMount';
import { PostingPlanHelpers } from '../../../modules/Publish/PostingPlan/postingPlanHelpers';

export type CalendarViewProps = {
  // The calendar cell template must accept 'any', but the templates themselves must pass in what they expect.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cellTemplate: (props: CellTemplateProps<any>) => JSX.Element;
  data: CalendarDataType<PostingPlanCellDataType | QueueCellDataType>;
  fixedHeader?: boolean;
  headerNames: CalendarHeaderNames;
  onScroll?: EventListener;
  timeFormat: TimeFormatSetting;
  renderCallback?: (elem: HTMLTableElement | null) => void;
  startDayOfWeek: StartOfWeekFormatSetting;
  augmentedClasses?: typeof defaultClasses; // Optional classes prop
};

export const CalendarView = (props: CalendarViewProps): JSX.Element => {
  const { cellTemplate: Template, data, headerNames, fixedHeader, timeFormat, augmentedClasses } = props;
  const classes = PostingPlanHelpers.mergedClasses(defaultClasses, augmentedClasses);
  const axisWidth = 68;
  const mediumCellFixedWidth = 150;
  const globalClasses = classNames(classes.Calendar, fixedHeader ? classes.CalendarFixed : null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarTableRef = useRef<HTMLTableElement | null>(null);
  const breakpoint = useBreakpoints();
  const { width } = useDimensions(calendarRef);
  const [widths, setWidths] = useState<CalendarHeaderWidths>([
    'auto',
    'auto',
    'auto',
    'auto',
    'auto',
    'auto',
    'auto',
    'auto'
  ]);

  useEffect(() => {
    if (width) {
      if (breakpoint === 'medium') {
        setWidths([
          axisWidth,
          mediumCellFixedWidth,
          mediumCellFixedWidth,
          mediumCellFixedWidth,
          mediumCellFixedWidth,
          mediumCellFixedWidth,
          mediumCellFixedWidth,
          mediumCellFixedWidth
        ]);
      } else {
        const cellSize = Math.round(((width - axisWidth) / 7 + Number.EPSILON) * 100) / 100;
        setWidths([axisWidth, cellSize, cellSize, cellSize, cellSize, cellSize, cellSize, cellSize]);
      }
    }
  }, [width, breakpoint]);

  useEffect(() => {
    if (calendarRef.current && fixedHeader && props.onScroll) {
      calendarRef.current.addEventListener('scroll', props.onScroll);
    }
  }, [calendarRef, fixedHeader, props.onScroll]);

  useDidMount(() => {
    return () => props.onScroll && calendarRef.current?.removeEventListener('scroll', props.onScroll);
  });

  useEffect(() => {
    if (calendarTableRef.current && props.renderCallback) {
      props.renderCallback(calendarTableRef.current);
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [calendarTableRef.current]);

  const rows = useMemo(
    () =>
      data.map((row, index) => (
        <tr key={index}>
          {row.map((cell, tdIndex) => (
            <Template
              key={tdIndex}
              position={[index, tdIndex]}
              row={cell}
              timeFormat={timeFormat}
              dayStartOfWeek={props.startDayOfWeek}
            />
          ))}
        </tr>
      )),
    [data, Template, timeFormat, props.startDayOfWeek]
  );

  return (
    <div className={globalClasses} ref={calendarRef}>
      <table data-scrollable={fixedHeader ? '' : null} ref={calendarTableRef}>
        <thead>
          <tr>
            {headerNames.map((name, index) => (
              <th key={`${index}-th`} style={{ width: widths[index] }}>
                {index !== 0 ? name : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
};
